import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useUserSearch, USER_SEARCH_MIN_CHARS, type UserSearchResult } from '@/hooks/useUsers';
import { useDropdownPosition } from '@/components/common/OuTree/useDropdownPosition';
import { InitialsAvatar } from '@/components/common/InitialsAvatar';
import './UserSearchSelect.css';

/** Selección emitida al elegir un usuario del desplegable. */
export interface UserSearchSelection {
  id: string;
  /** Anulable: se puede seleccionar a un frontline, y esas fichas no tienen correo. */
  email: string | null;
  displayName: string;
}

interface UserSearchSelectProps {
  /** Selección actual (null = sin seleccionar). */
  value: UserSearchSelection | null;
  /** Se emite al elegir una opción, o con null cuando el texto se edita y anula la selección. */
  onSelect: (selection: UserSearchSelection | null) => void;
  placeholder?: string;
  /** id del input para asociarlo a un <label htmlFor>. */
  inputId?: string;
}

const DEBOUNCE_MS = 300;

/** Combobox reutilizable de búsqueda de usuarios (nombre o correo) contra UserAdminService.
 *  Selección estricta: el consumidor decide qué hacer si value queda en null. */
export function UserSearchSelect({ value, onSelect, placeholder, inputId }: UserSearchSelectProps) {
  const { t } = useTranslation();
  const [inputText, setInputText] = useState(value?.displayName ?? '');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);

  const { data: results = [], isFetching, isError } = useUserSearch(debouncedTerm);
  const showDropdown = isOpen && !value && debouncedTerm.trim().length >= USER_SEARCH_MIN_CHARS;
  // Portal con posición fija (mismo patrón que OuTreeSelect variant="dropdown"): dentro del
  // flujo normal el desplegable quedaba recortado por contenedores con `overflow` propio, como
  // el cuerpo del modal de "Agregar administrador".
  const dropdownPosition = useDropdownPosition(rootRef, showDropdown);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(inputText), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputText]);

  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setIsOpen(false);
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  function handleChange(text: string) {
    setInputText(text);
    setIsOpen(true);
    setActiveIndex(-1);
    if (value) onSelect(null);
  }

  function selectResult(result: UserSearchResult) {
    onSelect({ id: result.id, email: result.email, displayName: result.name });
    setInputText(result.name);
    setIsOpen(false);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') { setIsOpen(false); return; }
    if (!isOpen || results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(prev => (prev + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(prev => (prev - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (activeIndex >= 0) selectResult(results[activeIndex]!);
    }
  }

  const dropdownStyle = dropdownPosition
    ? ({
        '--uss-dropdown-top': `${dropdownPosition.top}px`,
        '--uss-dropdown-left': `${dropdownPosition.left}px`,
        '--uss-dropdown-width': `${dropdownPosition.width}px`,
        '--uss-dropdown-max-height': `${dropdownPosition.maxHeight}px`,
      } as CSSProperties)
    : undefined;

  return (
    <div className="user-search-select" ref={rootRef}>
      <input
        id={inputId}
        type="text"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls="user-search-listbox"
        aria-autocomplete="list"
        className="form-input"
        placeholder={placeholder ?? t('admin.userSearchPlaceholder')}
        value={inputText}
        onChange={event => handleChange(event.target.value)}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
      />
      {showDropdown && dropdownPosition && createPortal(
        <ul className="uss-dropdown" id="user-search-listbox" role="listbox" style={dropdownStyle}>
          {isFetching && (
            <li className="uss-status"><i className="fas fa-spinner fa-spin" /></li>
          )}
          {isError && !isFetching && (
            <li className="uss-status uss-error">{t('admin.userSearchError')}</li>
          )}
          {!isFetching && !isError && results.length === 0 && (
            <li className="uss-status">{t('admin.userSearchNoResults')}</li>
          )}
          {!isError && results.map((result, index) => (
            <li
              key={result.id}
              role="option"
              aria-selected={index === activeIndex}
              className={`uss-option${index === activeIndex ? ' active' : ''}`}
              onMouseDown={() => selectResult(result)}
            >
              <InitialsAvatar name={result.name} baseClass="uss-avatar" colorIndex={index} />
              <div className="uss-option-text">
                <span className="uss-option-name">{result.name}</span>
                <span className="uss-option-email">{result.email}</span>
              </div>
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
}
